// The MIT License (MIT)
// Copyright (c) 2020 Brian Vaughn

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.

// https://github.com/bvaughn/react-error-boundary
// Sourced from the 'react-error-boundary by Bryan Vaughn under the MIT License
// Pulled into SNAP directly instead of as a dependecy with modifications

import React, { useEffect } from 'react'
// The ErrorBoundary class offers three ways of rendering if triggered
// 1) A default message
// 2) A custom component via the FallbackComponent prop
// 3) A function that generates a React component via the fallbackRenderFunction prop
//     This function takes the error and a function to reset the errorBoundary as an argument.

// It also allows you the reset the errorBoundary from the parent using the 'resetKeys' and
// on 'resetKeysChange' props. Take a look at the original docs for more details on how that works.
// Or just read the code in componentDidUpdate. This is for when the parent passes bad / no data that
// errorBoundary child depends on, causing it the crash. It does this by watching if the resetKeys ever change
// and triggering a rerender if they.
// ``useErrorHandler is a wrapper around a ReactState that throws the state value if is not null.
// It exposes the setState function to change the error. When using this provide a clear description
// string that says what the exception is as this does get logged to the console.

const initialState = { error: null }

export class ErrorBoundary extends React.Component {
  static getDerivedStateFromError(error) {
    return { error }
  }
  state = initialState
  resetErrorBoundary = (...args) => {
    this.reset()
    if (this.props.onReset) {
      this.props.onReset(...args)
    }
  }

  reset() {
    this.setState(initialState)
  }

  componentDidCatch(error, info) {
    console.error('Failed Network Request', error.response)
    console.error('Stack Trace', error.stack)
    console.error('INFO', info)
  }

  componentDidUpdate(prevProps, prevState) {
    const { error } = this.state
    const { resetKeys } = this.props
    if (
      error !== null &&
      prevState.error !== null &&
      wasArrayChanged(prevProps.resetKeys, resetKeys)
    ) {
      if (this.props.onResetKeysChange) {
        this.props.onResetKeysChange(prevProps.resetKeys, resetKeys)
      }
      this.reset()
    }
  }

  render() {
    const { error } = this.state

    const { fallbackRenderFunction, FallbackComponent } = this.props

    if (error !== null) {
      const props = {
        error,
        resetErrorBoundary: this.resetErrorBoundary,
      }
      if (fallbackRenderFunction) {
        return fallbackRenderFunction(props)
      } else if (FallbackComponent) {
        return <FallbackComponent {...props} />
      } else {
        return (
          <div className="card-myed d-flex flex-column justify-content-center align-items-center ">
            <h1>Oops that is embarrassing. Something broke.</h1>
          </div>
        )
      }
    }

    return this.props.children
  }
}

export function useWatchForError(state) {
  useEffect(() => {
    if (state != undefined) throw state
  }, [state])
}

function wasArrayChanged(a = [], b = []) {
  a.length !== b.length || a.some((item, index) => !Object.is(item, b[index]))
}
